import { Breadcrumb } from '@fcom/common-booking/interfaces/booking-header-interfaces';
import { NotificationTheme } from '@fcom/ui-components';

import { Stage } from '../interfaces/stage.interface';
import { UpgradeError } from '../interfaces/upgrade-error.interface';

export const UPGRADE_FEATURE_KEY = 'upgrade';

export const BREADCRUMBS: Breadcrumb[] = [
  {
    id: Stage.PASSENGERS,
    name: null,
    translationKey: 'passengers',
    active: false,
  },
  {
    id: Stage.FLIGHTS,
    name: null,
    translationKey: 'flights',
    active: false,
  },
  {
    id: Stage.REVIEW,
    name: null,
    translationKey: 'review',
    active: false,
  },
];

export const WAITLIST_CUTOFF = 60 * 24 * 60 * 60 * 1000; // 60 day in milliseonds

export const FINNAIR_LIKE_AIRLINECODES = ['N7', 'AY'];

export const OFFERS_V2_TIMEOUT = 30000; // V2Offers API has long processing times, so for now we add timeout for long queries

/**
 * If there is multiple errors then prioritise which one will be shown to the customer.
 */
export const UpgradeErrorPriority = [
  UpgradeError.HTTP_ERROR,
  UpgradeError.INVALID_BOOKING_REF,
  UpgradeError.UNKNOWN_ERROR,
  UpgradeError.GROUP_BOOKING,
  UpgradeError.SSR_IN_BOOKING,
  UpgradeError.INFANT_IN_BOOKING,
  UpgradeError.INELIGIBLE_BOOKING_CLASS,
  UpgradeError.TOO_MANY_PASSENGERS,
  UpgradeError.BOOKING_IN_INELIGIBLE_STATUS,
  UpgradeError.FAILED,
  UpgradeError.IN_PROGRESS,
  UpgradeError.ON_WAITLIST,
  UpgradeError.ALREADY_UPGRADED,
  UpgradeError.NO_REMAINING_UPGRADES,
  UpgradeError.INSUFFICIENT_PAYMENT,
  UpgradeError.TOO_CLOSE_TO_DEPARTURE,
  UpgradeError.TOO_CLOSE_TO_DEPARTURE_FOR_WAITLIST,
  UpgradeError.TOO_FAR_TO_DEPARTURE,
  UpgradeError.SEGMENT_IN_REQUESTED_UPGRADE_CABIN,
  UpgradeError.TICKET_NOT_ISSUED_BY_FINNAIR,
  UpgradeError.NOT_FINNAIR_FLIGHT,
  UpgradeError.NOT_OPERATED_BY_FINNAIR,
  UpgradeError.FLOWN_SEGMENT,
  UpgradeError.INSUFFICIENT_SEATS,
  UpgradeError.NOT_LOGGED_IN,
  UpgradeError.TRAIN_CONNECTION,
  UpgradeError.BUS_CONNECTION,
  UpgradeError.SEGMENT_MISSING_REQUESTED_UPGRADE_CABIN,
];

/**
 * If any flights have one of these statuses then the user cannot enter the flow at all.
 */
export const UPGRADE_BLOCKING_ERRORS = [
  UpgradeError.INFANT_IN_BOOKING,
  UpgradeError.GROUP_BOOKING,
  UpgradeError.SSR_IN_BOOKING,
  UpgradeError.INSUFFICIENT_PAYMENT,
  UpgradeError.IN_PROGRESS,
  UpgradeError.UNKNOWN_ERROR,
  UpgradeError.BOOKING_IN_INELIGIBLE_STATUS,
  UpgradeError.TICKET_NOT_ISSUED_BY_FINNAIR,
  UpgradeError.TOO_MANY_PASSENGERS,
  UpgradeError.HTTP_ERROR,
  UpgradeError.FAILED,
  UpgradeError.NOT_LOGGED_IN,
];

export const upgradeErrorMap = {
  [UpgradeError.HTTP_ERROR]: {
    heading: 'oneUpgrade.errors.httpError.heading',
    paragraph: 'oneUpgrade.errors.httpError.paragraph',
    theme: NotificationTheme.ALERT,
  },
  [UpgradeError.INVALID_BOOKING_REF]: {
    paragraph: 'oneUpgrade.login.notFound',
    theme: NotificationTheme.ALERT,
  },
  [UpgradeError.INFANT_IN_BOOKING]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.GROUP_BOOKING]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.SSR_IN_BOOKING]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.INSUFFICIENT_PAYMENT]: {
    heading: 'oneUpgrade.errors.insufficientPayment.heading',
    paragraph: 'oneUpgrade.errors.insufficientPayment.paragraph',
  },
  [UpgradeError.IN_PROGRESS]: {
    heading: 'oneUpgrade.errors.inProgress.heading',
    paragraph: 'oneUpgrade.errors.inProgress.paragraph',
  },
  [UpgradeError.TOO_MANY_PASSENGERS]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.TOO_CLOSE_TO_DEPARTURE_FOR_WAITLIST]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.TOO_CLOSE_TO_DEPARTURE]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.INELIGIBLE_BOOKING_CLASS]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.SEGMENT_IN_REQUESTED_UPGRADE_CABIN]: {
    heading: 'oneUpgrade.errors.alreadyInCabin.heading',
    paragraph: 'oneUpgrade.errors.alreadyInCabin.paragraph',
  },
  [UpgradeError.ONLY_ECONOMY_AVAILABLE]: {
    heading: 'oneUpgrade.errors.onlyEconomyAvailable.heading',
    paragraph: 'oneUpgrade.errors.onlyEconomyAvailable.paragraph',
  },
  [UpgradeError.SEGMENT_MISSING_REQUESTED_UPGRADE_CABIN]: {
    paragraph: 'oneUpgrade.errors.unavailableCabin.paragraph',
  },
  [UpgradeError.TOO_FAR_TO_DEPARTURE]: {
    heading: 'oneUpgrade.errors.tooFarToDeparture.heading',
    paragraph: 'oneUpgrade.errors.tooFarToDeparture.paragraph',
  },
  [UpgradeError.BOOKING_IN_INELIGIBLE_STATUS]: {
    heading: 'oneUpgrade.errors.bookingInIneligibleStatus.heading',
    paragraph: 'oneUpgrade.errors.bookingInIneligibleStatus.paragraph',
  },
  [UpgradeError.NOT_FINNAIR_FLIGHT]: {
    heading: 'oneUpgrade.errors.notFinnair.heading',
    paragraph: 'oneUpgrade.errors.notFinnair.paragraph',
  },
  [UpgradeError.NOT_OPERATED_BY_FINNAIR]: {
    heading: 'oneUpgrade.errors.notFinnair.heading',
    paragraph: 'oneUpgrade.errors.notFinnair.paragraph',
  },
  [UpgradeError.FLOWN_SEGMENT]: {
    heading: 'oneUpgrade.errors.alreadyFlown.heading',
    paragraph: 'oneUpgrade.errors.alreadyFlown.paragraph',
  },
  [UpgradeError.TICKET_NOT_ISSUED_BY_FINNAIR]: {
    heading: 'oneUpgrade.errors.ticketNotIssuedByFinnair.heading',
    paragraph: 'oneUpgrade.errors.ticketNotIssuedByFinnair.paragraph',
  },
  [UpgradeError.UNKNOWN_ERROR]: {
    heading: 'oneUpgrade.errors.notAvailableOnline.heading',
    paragraph: 'oneUpgrade.errors.notAvailableOnline.paragraph',
  },
  [UpgradeError.ON_WAITLIST]: {
    heading: 'oneUpgrade.errors.onWaitlist.heading',
    paragraph: 'oneUpgrade.errors.onWaitlist.paragraph',
  },
  [UpgradeError.ALREADY_UPGRADED]: {
    heading: 'oneUpgrade.errors.alreadyUpgraded.heading',
    paragraph: 'oneUpgrade.errors.alreadyUpgraded.paragraph',
    theme: NotificationTheme.SUCCESS,
  },
  [UpgradeError.FAILED]: {
    heading: 'oneUpgrade.errors.upgradeFailed.heading',
    paragraph: 'oneUpgrade.errors.upgradeFailed.paragraph',
    theme: NotificationTheme.ALERT,
  },
  [UpgradeError.INSUFFICIENT_SEATS]: {
    heading: 'oneUpgrade.errors.insufficientSeats.heading',
    paragraph: 'oneUpgrade.errors.insufficientSeats.paragraph',
  },
  [UpgradeError.TRAIN_CONNECTION]: {
    heading: 'oneUpgrade.errors.groundTransportationTrain.heading',
    paragraph: 'oneUpgrade.errors.groundTransportationTrain.paragraph',
  },
  [UpgradeError.BUS_CONNECTION]: {
    heading: 'oneUpgrade.errors.groundTransportationBus.heading',
    paragraph: 'oneUpgrade.errors.groundTransportationBus.paragraph',
  },
  [UpgradeError.NO_REMAINING_UPGRADES]: {
    heading: 'oneUpgrade.errors.noRemainingUpgrades.heading',
    paragraph: 'oneUpgrade.errors.noRemainingUpgrades.paragraph',
  },
  [UpgradeError.PAYMENT_ERROR]: {
    heading: 'oneUpgrade.errors.paymentError.heading',
    paragraph: 'oneUpgrade.errors.paymentError.paragraph',
  },
  [UpgradeError.NOT_LOGGED_IN]: {
    heading: 'oneUpgrade.errors.notLoggedIn.heading',
    paragraph: 'oneUpgrade.errors.notLoggedIn.paragraph',
  },
};

export const InvalidBookingReference = 'Invalid booking reference';
